import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Users, 
  QrCode,
  Calendar,
  MapPin,
  Shield,
  ScanLine,
  Settings,
  Check,
  X,
  Loader2,
  Bell,
  Clock,
  ChevronRight
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const roleIcons = {
  scanner: ScanLine,
  manager: Settings,
  admin: Shield
};

const roleColors = {
  scanner: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  manager: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  admin: 'bg-amber-500/20 text-amber-400 border-amber-500/30'
};

const roleDescriptions = {
  scanner: 'Scan tickets at events',
  manager: 'Manage events & scan tickets',
  admin: 'Full account access'
};

export default function TeamDashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [teams, setTeams] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('teams');
  const [processingInvite, setProcessingInvite] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (!authLoading && isAuthenticated) {
      fetchData();
    }
  }, [authLoading, isAuthenticated]);

  const fetchData = async () => {
    try {
      const [teamsRes, invitesRes] = await Promise.all([
        axios.get(`${API_URL}/team/my-teams`),
        axios.get(`${API_URL}/team/pending-invites`)
      ]);
      setTeams(teamsRes.data || []);
      setPendingInvites(invitesRes.data || []);
    } catch (error) {
      console.error('Error fetching team data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvite = async (inviteToken) => {
    setProcessingInvite(inviteToken);
    try {
      await axios.post(`${API_URL}/team/accept/${inviteToken}`);
      toast.success('Invitation accepted! You are now a team member.');
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setProcessingInvite(null);
    }
  };

  const handleRejectInvite = async (inviteToken) => {
    setProcessingInvite(inviteToken);
    try {
      await axios.post(`${API_URL}/team/reject/${inviteToken}`);
      toast.info('Invitation declined');
      setPendingInvites(pendingInvites.filter(i => i.invite_token !== inviteToken));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline invitation');
    } finally {
      setProcessingInvite(null);
    }
  };

  const formatEventDate = (dateStr) => {
    if (!dateStr) return 'TBA';
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-8 h-8 text-primary" />
            <h1 className="font-heading font-bold text-3xl">Team Dashboard</h1>
          </div>
          <p className="text-muted-foreground">
            Manage your team memberships and scan tickets for events
          </p>
        </div>

        {/* Pending Invites Banner */}
        {pendingInvites.length > 0 && (
          <Card className="bg-primary/10 border-primary/30 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-primary" />
                <span className="font-medium">
                  You have {pendingInvites.length} pending team invitation{pendingInvites.length > 1 ? 's' : ''}
                </span>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setActiveTab('invites')}
                  className="ml-auto"
                >
                  View Invites
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="teams" className="gap-2">
              <Users className="w-4 h-4" />
              My Teams ({teams.length})
            </TabsTrigger>
            <TabsTrigger value="invites" className="gap-2">
              <Bell className="w-4 h-4" />
              Invitations ({pendingInvites.length})
            </TabsTrigger>
          </TabsList>

          {/* Teams Tab */}
          <TabsContent value="teams">
            {teams.length === 0 ? (
              <Card className="bg-card border-white/10">
                <CardContent className="py-12 text-center">
                  <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="font-heading font-semibold text-lg mb-2">
                    Not part of any teams yet
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    When a creator adds you to their team, you'll see it here
                  </p>
                  {pendingInvites.length > 0 && (
                    <Button onClick={() => setActiveTab('invites')}>
                      View {pendingInvites.length} Pending Invite{pendingInvites.length > 1 ? 's' : ''}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {teams.map((team) => {
                  const RoleIcon = roleIcons[team.role] || Users;
                  return (
                    <Card key={team.id} className="bg-card border-white/10" data-testid={`team-${team.id}`}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Avatar className="w-12 h-12">
                              <AvatarImage src={team.creator?.avatar_url} />
                              <AvatarFallback className="bg-primary/20 text-primary">
                                {team.creator?.display_name?.charAt(0) || '?'}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <CardTitle className="text-lg">{team.creator?.display_name}'s Team</CardTitle>
                              <p className="text-sm text-muted-foreground">
                                @{team.creator?.username?.replace('@', '') || 'creator'}
                              </p>
                            </div>
                          </div>
                          <Badge className={roleColors[team.role]}>
                            <RoleIcon className="w-3 h-3 mr-1" />
                            {team.role?.charAt(0).toUpperCase() + team.role?.slice(1)}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-4">
                          {roleDescriptions[team.role]}
                        </p>

                        {/* Assigned Events */}
                        <div className="mb-4">
                          <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            Events ({team.assigned_events?.length || 0})
                          </h4>
                          
                          {team.assigned_events?.length > 0 ? (
                            <div className="space-y-2">
                              {team.assigned_events.slice(0, 3).map((event) => (
                                <div 
                                  key={event.id}
                                  className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg"
                                >
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate">{event.title}</p>
                                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                      <span className="flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        {formatEventDate(event.event_date)}
                                      </span>
                                      {event.location && (
                                        <span className="flex items-center gap-1">
                                          <MapPin className="w-3 h-3" />
                                          {event.location}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <Button 
                                    size="sm"
                                    onClick={() => navigate(`/scanner/${event.id}`)}
                                    className="ml-2"
                                  >
                                    <QrCode className="w-4 h-4 mr-1" />
                                    Scan
                                  </Button>
                                </div>
                              ))}
                              {team.assigned_events.length > 3 && (
                                <Button 
                                  variant="ghost" 
                                  className="w-full"
                                  onClick={() => navigate(`/scanner`)}
                                >
                                  View all {team.assigned_events.length} events
                                  <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground text-center py-4">
                              No events assigned yet
                            </p>
                          )}
                        </div>

                        {/* Quick Actions */}
                        <div className="flex gap-2">
                          <Button 
                            className="flex-1"
                            onClick={() => navigate('/scanner')}
                          >
                            <QrCode className="w-4 h-4 mr-2" />
                            Open Scanner
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={() => navigate(`/creator/${team.creator?.id}`)}
                          >
                            View Profile
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Invitations Tab */}
          <TabsContent value="invites">
            {pendingInvites.length === 0 ? (
              <Card className="bg-card border-white/10">
                <CardContent className="py-12 text-center">
                  <Bell className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="font-heading font-semibold text-lg mb-2">
                    No pending invitations
                  </h3>
                  <p className="text-muted-foreground">
                    When a creator invites you to their team, you'll see it here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingInvites.map((invite) => {
                  const RoleIcon = roleIcons[invite.role] || Users;
                  const isProcessing = processingInvite === invite.invite_token;
                  
                  return (
                    <Card key={invite.id} className="bg-card border-white/10" data-testid={`invite-${invite.id}`}>
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <Avatar className="w-14 h-14">
                            <AvatarImage src={invite.creator?.avatar_url} />
                            <AvatarFallback className="bg-primary/20 text-primary text-lg">
                              {invite.creator?.display_name?.charAt(0) || '?'}
                            </AvatarFallback>
                          </Avatar>
                          
                          <div className="flex-1">
                            <h3 className="font-heading font-semibold text-lg mb-1">
                              {invite.creator?.display_name} invited you to join their team
                            </h3>
                            <p className="text-sm text-muted-foreground mb-3">
                              @{invite.creator?.username?.replace('@', '') || 'creator'}
                            </p>
                            
                            <div className="flex items-center gap-4 mb-4">
                              <Badge className={roleColors[invite.role]}>
                                <RoleIcon className="w-3 h-3 mr-1" />
                                {invite.role?.charAt(0).toUpperCase() + invite.role?.slice(1)}
                              </Badge>
                              <span className="text-sm text-muted-foreground flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {roleDescriptions[invite.role]}
                              </span>
                            </div>
                            
                            <div className="flex gap-3">
                              <Button 
                                onClick={() => handleAcceptInvite(invite.invite_token)}
                                disabled={isProcessing}
                                className="gap-2"
                              >
                                {isProcessing ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Check className="w-4 h-4" />
                                )}
                                Accept
                              </Button>
                              <Button 
                                variant="outline"
                                onClick={() => handleRejectInvite(invite.invite_token)}
                                disabled={isProcessing}
                                className="gap-2"
                              >
                                <X className="w-4 h-4" />
                                Decline
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
